import React, { useState } from "react";
import { motion } from "framer-motion";
import { Maximize2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useApiCollection } from "../hooks/useApiCollection";
import { MEDIA } from "../lib/site";

const SIZE_MAP = {
  large: "md:col-span-8 md:row-span-2",
  wide: "md:col-span-6",
  half: "md:col-span-6",
  small: "md:col-span-4",
};

export default function Gallery() {
  const { t, lang } = useLanguage();
  const { data: items } = useApiCollection("/gallery", []);
  const [lightbox, setLightbox] = useState(null); // index | null

  const open = (i) => setLightbox(i);
  const close = () => setLightbox(null);
  const next = () => setLightbox((i) => (i + 1) % items.length);
  const prev = () => setLightbox((i) => (i - 1 + items.length) % items.length);

  React.useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox, items.length]);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative bg-[#0A0A0A] py-24 sm:py-32 overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: `url(${MEDIA.topo})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-12 gap-8 mb-12">
          <div className="col-span-12 md:col-span-7">
            <div className="overline mb-4">{t.gallery.overline}</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-white">
              {t.gallery.title}
            </h2>
            <p className="mt-6 text-neutral-400 text-base sm:text-lg max-w-2xl leading-relaxed">
              {t.gallery.subtitle}
            </p>
          </div>
          <div className="col-span-12 md:col-span-5 flex flex-wrap gap-2 md:justify-end items-end">
            {Array.from(new Set(items.map((it) => (lang === "ro" ? it.tag_ro : it.tag_en)).filter(Boolean))).slice(0, 5).map((tag, i) => (
              <span
                key={tag}
                data-testid={`gallery-tag-${i}`}
                className="font-mono-tech text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-neutral-800 text-neutral-400"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 md:auto-rows-[180px] gap-3">
          {items.map((it, idx) => {
            const span = SIZE_MAP[it.size] || SIZE_MAP.small;
            const tag = lang === "ro" ? it.tag_ro : it.tag_en;
            return (
              <motion.button
                key={it.id}
                onClick={() => open(idx)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: idx * 0.07 }}
                data-testid={`gallery-item-${it.slug}`}
                className={`relative group border border-neutral-800 overflow-hidden aspect-[4/3] md:aspect-auto text-left ${span}`}
              >
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: `url(${it.image_url})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-90 group-hover:opacity-70 transition-opacity" />
                <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-2">
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-yellow-400">
                    / {String(idx).padStart(2, "0")}
                    {tag && <span className="text-neutral-300"> · {tag}</span>}
                  </div>
                  <Maximize2 className="h-3.5 w-3.5 text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {lightbox !== null && items[lightbox] && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
          onClick={close}
        >
          <button
            onClick={close}
            data-testid="gallery-lightbox-close"
            className="absolute top-4 right-4 h-10 w-10 flex items-center justify-center bg-neutral-900 border border-neutral-700 text-white hover:bg-yellow-500 hover:text-black z-10"
            aria-label="Închide"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="relative w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-yellow-400 mb-3">
              / {String(lightbox).padStart(2, "0")}
              {items[lightbox] && (lang === "ro" ? items[lightbox].tag_ro : items[lightbox].tag_en) && (
                <span className="text-neutral-300"> · {lang === "ro" ? items[lightbox].tag_ro : items[lightbox].tag_en}</span>
              )}
            </div>
            <div className="font-display font-semibold text-white text-xl sm:text-2xl mb-5">
              {lang === "ro" ? items[lightbox].title_ro : items[lightbox].title_en}
            </div>
            <div className="relative">
              <div className="aspect-video bg-black border border-neutral-800 overflow-hidden flex items-center justify-center max-h-[80vh]">
                <img
                  src={items[lightbox].image_url}
                  alt={items[lightbox].title_ro}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              {items.length > 1 && (
                <>
                  <button
                    onClick={prev}
                    className="absolute top-1/2 -translate-y-1/2 left-3 h-11 w-11 bg-black/70 border border-neutral-700 text-white hover:bg-yellow-500 hover:text-black flex items-center justify-center"
                    aria-label="Anterior"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={next}
                    className="absolute top-1/2 -translate-y-1/2 right-3 h-11 w-11 bg-black/70 border border-neutral-700 text-white hover:bg-yellow-500 hover:text-black flex items-center justify-center"
                    aria-label="Următor"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  <div className="mt-4 flex items-center justify-center gap-1.5">
                    {items.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setLightbox(i)}
                        className={`h-1.5 transition-all ${
                          i === lightbox ? "w-8 bg-yellow-500" : "w-2.5 bg-neutral-700 hover:bg-neutral-500"
                        }`}
                        aria-label={`Foto ${i + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
